import { useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { python } from '@codemirror/lang-python'
import { java } from '@codemirror/lang-java'
import { sql } from '@codemirror/lang-sql'
import { php } from '@codemirror/lang-php'
import { rust } from '@codemirror/lang-rust'
import { go } from '@codemirror/lang-go'
import { html } from '@codemirror/lang-html'
import { css } from '@codemirror/lang-css'
import type { Extension } from '@codemirror/state'
import type { ExerciseCode as ExerciseCodeType } from '../types'
import { useTranslation } from 'react-i18next'
import { useExerciseSlot } from '../hooks/useExerciseSet'

interface ExerciseCodeProps {
  exercise: ExerciseCodeType
  /** Position dans la série d'exercices du message — voir `ExerciseSetProvider`. */
  slotIndex: number
}

const NO_ANSWER = '—'

const LANGUAGE_LABELS: Record<string, string> = {
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  python: 'Python',
  java: 'Java',
  css: 'CSS',
  html: 'HTML',
  php: 'PHP',
  rust: 'Rust',
  go: 'Go',
  sql: 'SQL',
}

const LANGUAGE_ICONS: Record<string, string> = {
  javascript: '⚡',
  typescript: '🔷',
  python: '🐍',
  java: '☕',
  css: '🎨',
  html: '🌐',
  php: '🐘',
  rust: '🦀',
  go: '🐹',
  sql: '🗃️',
}

function getExtension(lang: string): Extension[] {
  switch (lang) {
    case 'javascript':
      return [javascript()]
    case 'typescript':
      return [javascript({ typescript: true })]
    case 'python':
      return [python()]
    case 'java':
      return [java()]
    case 'sql':
      return [sql()]
    case 'php':
      return [php()]
    case 'rust':
      return [rust()]
    case 'go':
      return [go()]
    case 'html':
      return [html()]
    case 'css':
      return [css()]
    default:
      return []
  }
}

export function ExerciseCode({ exercise, slotIndex }: Readonly<ExerciseCodeProps>) {
  const { t } = useTranslation()
  const [code, setCode] = useState(exercise.starter)
  const [language, setLanguage] = useState(exercise.language)

  const hasAnswer = code.trim().length > 0 && code.trim() !== exercise.starter.trim()

  const { locked } = useExerciseSlot(slotIndex, hasAnswer, () => {
    const trimmed = code.trim()
    return {
      itemType: 'code',
      question: exercise.question,
      answerLabel: trimmed ? `\`\`\`${language}\n${trimmed}\n\`\`\`` : `"${NO_ANSWER}"`,
      correct: null,
      answered: hasAnswer,
    }
  })

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-[var(--color-neutral-900)] flex-1">{exercise.question}</p>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={locked}
          className="shrink-0 text-xs border border-gray-200 rounded-md px-2 py-1 bg-white text-[var(--color-neutral-700)] focus:outline-none focus:border-[var(--color-primary)] disabled:opacity-50 cursor-pointer min-h-[32px]"
          aria-label={t('session.programming_language')}
        >
          {Object.entries(LANGUAGE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {LANGUAGE_ICONS[key] ?? ''} {label}
            </option>
          ))}
        </select>
      </div>

      <div className="exercise-code-editor rounded-lg overflow-hidden border border-gray-200">
        <CodeMirror
          value={code}
          height="180px"
          extensions={getExtension(language)}
          onChange={(val) => {
            if (!locked) setCode(val)
          }}
          editable={!locked}
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: true,
          }}
          className="text-xs"
        />
      </div>

      {locked && (
        <p className="text-xs text-gray-400">{t('session.exercise_pending_feedback')}</p>
      )}
    </div>
  )
}
